/**
 * Rule proposals, from a manager's first draft to the recorded vote.
 *
 * The league already runs this process; it just runs it across Discord, a Google Doc and
 * somebody's memory. The shape below is the shape it already has:
 *
 *   1  A manager writes a DRAFT. Private until he submits it, because half of them get
 *      abandoned and nobody needs to read those.
 *   2  He SUBMITS it to the commissioners before the deadline.
 *   3  The commissioners tidy the wording — which they do every year anyway, and which
 *      2019-I-2 §2 explicitly allows — and put it on the DOCKET in a running order.
 *   4  The docket is PUBLISHED. Now the league can read it, which is the point of
 *      2022-committee-quorum-voting §1: absent GMs need it in advance so they can vote.
 *   5  In the meeting, votes are recorded and the outcome falls out of the thresholds.
 *
 * The thresholds live in src/domain/proposals.ts, because they are rules rather than UI. What
 * this screen adds is the live readout — "one more yes for a permanent pass" — which is the
 * thing the room actually wants to know while hands are up.
 */

import { useEffect, useMemo, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import {
  LEAGUE_SIZE, branchFrom, proposalsFor,
} from '../domain/proposals.js';
import { DocketOrder } from './DocketOrder.js';
import { Docket as DocketCard } from './Docket.js';
import { sameIntent, staleDrafts } from '../domain/docket.js';
import { roomStatus } from '../domain/room.js';
import gistData from '../data/gists-2026.json';
import { Meeting } from './Meeting.js';
import { useRulebook } from './useRulebook.js';
import { CommishConsole } from './CommishConsole.js';
import { Transcribed } from './Transcribed.js';
import type { ConsoleAct } from '../domain/commishConsole.js';
import {
  PHASE_LABEL, type Phase, canUnpublish, commitPlan, meetingHappened, notStarted, openItem,
  phaseOfMeeting, recordedProposal, unpublished, unstartMeeting,
} from '../domain/meeting.js';

import { DeclareEffect } from './DeclareEffect.js';
import type { SeasonFacts } from '../domain/effects.js';
import { bothZones, gateOf } from '../domain/deadlines.js';
import { GmBadge } from './GmBadge.js';
import { GMS } from '../store/session.js';
import { GateTag } from './Gate.js';
import { Citations, Linkify } from './RuleText.js';
import { RichEditor, RichView } from './Rich.js';
import { RevoteItems } from './RevoteItems.js';
import { carryoverRules,
  appendToOrder, carryoverProposal, missingCarryovers,
} from '../domain/carryovers.js';
import { splitRuleTitle } from '../domain/revote.js';
import type { Override } from '../domain/overrides.js';
import { isEmptyRich, isRich, plainToRich, sanitizeRich } from '../domain/richText.js';
import { note } from './auditor.js';
import type { ProxyBallot } from '../domain/proxy.js';
import { ProxyPanel, ProxyVisibility } from './ProxyPanel.js';
import { isLodged, instructedCount } from '../domain/proxy.js';
import {
  atLeast, type Docket, type DraftState, type FarmNomination, type KeeperDeclaration,
  type LeagueSettings, type LeagueStore, type Role, type RuleProposal,
} from '../store/types.js';

const SEASON = 2026;

const STATUS_LABEL: Record<RuleProposal['status'], string> = {
  draft: 'Draft', submitted: 'Submitted', docketed: 'On the docket',
  passed: 'Passed', provisional: 'Provisional', failed: 'Failed', withdrawn: 'Withdrawn',
};

interface Form {
  title: string;
  body: string;
  amends: string;
  /** Commissioner transcribing for somebody else. Empty means "mine". */
  onBehalfOf?: string;
  enteredVia?: string;
}

const blank = (): Form => ({ title: '', body: '', amends: '' });

/**
 * A stored body, ready for the editor.
 *
 * Everything written before the editor existed is plain text whose meaning is carried by its
 * blank lines. Handing that to a contentEditable raw would collapse every one of them into a
 * single run-on paragraph the moment somebody opened an old proposal to fix a typo — so the
 * paragraphs are made explicit on the way in. Bodies already in markup pass through untouched.
 */
const openBody = (body: string) => (isRich(body) ? body : plainToRich(body));

/**
 * The rules that lapse this year unless the committee re-votes them through.
 *
 * Read from the rulebook rather than listed by hand: `provisional` is a field on every entry and
 * the two 2025 conditional passes carry it. A hand-written list here would be a second place to
 * forget one.
 */
/*
 * NOT A MODULE CONSTANT ANY MORE. It was `RULES.filter(...)` computed once at import, which meant
 * the carryover list could not respond to the committee that had just decided it: two rules
 * re-voted through on 26 August stayed on this list, and would have been offered for re-vote again
 * in 2027 having already won one. It is derived per render from the effective corpus now — see
 * `useRulebook` — and the predicate lives in `carryoverRules`, which is the same one
 * `missingCarryovers` uses.
 */

export function Proposals(
  { gm, actor, role, store }:
  { gm: string; actor: string; role: Role; store: LeagueStore },
) {
  const [proposals, setProposals] = useState<RuleProposal[]>([]);
  const [docket, setDocket] = useState<Docket | null>(null);
  /** Which draft has been asked about before being thrown away. One at a time. */
  const [arming, setArming] = useState<string | null>(null);
  /* This GM's own ballot, and every LODGED one — see `firestore.rules`, which is what makes the
     second of those a narrower read than it looks. */
  const [myProxy, setMyProxy] = useState<ProxyBallot | null>(null);
  const [proxies, setProxies] = useState<ProxyBallot[]>([]);
  const [settings, setSettings] = useState<LeagueSettings | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState(blank());
  const [pane, setPane] = useState<'mine' | 'docket'>('mine');
  const [declarations, setDeclarations] = useState<KeeperDeclaration[]>([]);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [farm, setFarm] = useState<FarmNomination[]>([]);
  const [overrides, setOverrides] = useState<Override[]>([]);

  const commish = atLeast(role, 'commissioner');
  const book = useRulebook(store, SEASON);

  useEffect(() => {
    let live = true;
    const load = () => Promise.all([
      store.proposals(SEASON), store.docket(SEASON), store.settings(SEASON),
      store.declarations(SEASON), store.draftState(SEASON), store.farmNominations(SEASON),
      store.overrides(SEASON), store.proxyFor(SEASON, gm.toUpperCase()), store.proxies(SEASON),
    ]).then(([ps, d, s, decls, ds, fn, ov, mp, px]) => {
      if (!live) return;
      setProposals(ps); setDocket(d); setSettings(s);
      setDeclarations(decls); setDraft(ds); setFarm(fn); setOverrides(ov);
      setMyProxy(mp); setProxies(px);
    });
    void load();
    const off = store.subscribe(SEASON, (what) => {
      if (what === 'proposals' || what === 'docket' || what === 'settings'
        || what === 'overrides' || what === 'proxies') void load();
    });
    return () => { live = false; off(); };
  }, [store, gm]);

  /* Proposals have a real lock and a held-open state since Pass 15; `gateOf` reads all three. */
  const gate = gateOf(settings, 'proposals', Date.now());

  /*
   * Where the offseason has got to, which is what decides whether a change can name THIS season.
   * The committee meets on 26 August: the farm auction is still ahead of it and keeper
   * declarations are not, so the two questions get different answers on the same evening.
   */
  const seasonFacts: SeasonFacts = useMemo(() => ({
    declarationsStarted: declarations.some((d) => d.status === 'submitted'),
    draftRun: Boolean(draft?.status === 'complete' || draft?.status === 'live'),
    farmRun: farm.some((f) => f.winner),
    seasonStarted: Boolean(settings?.draftDate && Date.now() > new Date(settings.draftDate).getTime()),
  }), [declarations, draft, farm, settings]);
  const me = gm.toUpperCase();
  const mine = proposalsFor(proposals, me);
  const published = Boolean(docket?.publishedAt);
  const phase = phaseOfMeeting(docket);

  /** What the league at large can see: the published docket, in the commissioners' order. */
  const agenda = useMemo(() => {
    const ids = docket?.order ?? [];
    const byId = new Map(proposals.map((p) => [p.id, p]));
    return ids.map((id) => byId.get(id)).filter(Boolean) as RuleProposal[];
  }, [docket, proposals]);

  /*
   * THE TWO RULES NOBODY HAS TO REMEMBER, and which were therefore forgotten. See
   * `domain/carryovers.ts` - they were rulebook entries, never docket items, so the agenda came
   * out one item long. Recomputed from what is actually stored, so adding them is idempotent.
   */
  const revotes = useMemo(() => carryoverRules(book.rules), [book.rules]);
  const missing = useMemo(
    () => missingCarryovers(revotes, proposals, SEASON),
    [revotes, proposals],
  );

  /** Writes any absent carryover onto the docket. Returns the order it should now have. */
  async function seedCarryovers(): Promise<string[]> {
    const now = new Date().toISOString();
    const made = missing.map((r) => carryoverProposal(r, overrides, SEASON, now, GMS));
    for (const p of made) await store.saveProposal(p);
    return appendToOrder(docket?.order ?? [], made.map((p) => p.id));
  }

  const inbox = proposals.filter((p) => p.status === 'submitted');

  /*
   * WHAT THE WRITING CARD STILL HOLDS: your own proposals that have NOT reached the docket. The
   * ones that have are on it, once, and a second full rendering of them here is defect 1.
   */
  const onDocket = useMemo(() => new Set(agenda.map((p) => p.id)), [agenda]);
  const unsent = mine.filter((p) => !onDocket.has(p.id));

  /*
   * THE DRAFT THAT IS REALLY THE SAME PROPOSAL. Matched by intent rather than by title equality,
   * because a commissioner tidying the wording before it goes on the agenda is the normal case —
   * see `staleDrafts`. One line inside the docketed row, with one action.
   */
  const staleDraftsHere = useMemo(
    () => staleDrafts(unsent.filter((p) => p.status === 'draft'), agenda),
    [unsent, agenda],
  );

  /* The room's own count, so the docket's threshold and the room's cannot disagree. */
  const room = roomStatus({
    gms: GMS,
    absent: docket?.absent ?? [],
    proxies,
    order: agenda.map((p) => p.id),
    openItemId: docket?.openItemId,
    closedIds: docket?.closedIds,
  });

  /** One authored sentence per item, or none. Never derived from the body. */
  const GISTS: Record<string, string> = useMemo(() => {
    const byTitle = (gistData as { gists: Record<string, string> }).gists;
    const out: Record<string, string> = {};
    for (const p of agenda) {
      const g = byTitle[p.title];
      if (g) out[p.id] = g;
    }
    return out;
  }, [agenda]);

  /**
   * RECORDING FROM THE BAR, which is the same act the item's own Record button performs — both
   * call `recordedProposal`, so the two paths cannot disagree about the threshold on the night.
   */
  async function recordFromBar(p: RuleProposal) {
    await store.saveProposal(recordedProposal(p, room.represented, new Date().toISOString()));
  }

  /** Deleting one of your own drafts, with the record the audit log wants. */
  async function discard(p: RuleProposal) {
    note(store, actor, {
      kind: 'delete',
      what: `Deleted a draft proposal — ${p.title}`,
      target: `proposals/${p.id}`,
    });
    await store.deleteProposal(SEASON, p.id);
  }

  async function save(status: RuleProposal['status'], existing?: RuleProposal) {
    if (!form.title.trim() || isEmptyRich(form.body)) return;
    const now = new Date().toISOString();
    /*
     * `gm` IS THE AUTHOR, always. On a new proposal a commissioner may name somebody else, and
     * then `enteredBy` says who typed it. On an existing one the author never moves: re-saving
     * a transcribed proposal must not quietly reassign it to whoever opened the editor.
     */
    const author = existing?.gm ?? (form.onBehalfOf || gm);
    const transcribed = !existing && Boolean(form.onBehalfOf)
      && form.onBehalfOf!.toUpperCase() !== actor.toUpperCase();
    await store.saveProposal({
      id: existing?.id ?? `${Date.now()}`,
      season: SEASON,
      gm: author,
      title: form.title.trim(),
      body: sanitizeRich(form.body),
      amends: form.amends.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean),
      status,
      ...(existing?.votes ? { votes: existing.votes } : {}),
      ...(existing?.enteredBy
        ? {
          enteredBy: existing.enteredBy,
          ...(existing.enteredAt ? { enteredAt: existing.enteredAt } : {}),
          ...(existing.enteredVia ? { enteredVia: existing.enteredVia } : {}),
        }
        : {}),
      ...(transcribed
        ? {
          enteredBy: actor,
          enteredAt: now,
          ...(form.enteredVia?.trim() ? { enteredVia: form.enteredVia.trim() } : {}),
        }
        : {}),
      /*
       * `editedBy` means the WORDING was changed by somebody other than the author, which is the
       * thing 2020-I-1 §2 gives him a veto over. Transcribing is not that — it is the proposal
       * arriving — so a freshly entered one must not be born already marked as edited.
       */
      ...(existing && existing.gm.toUpperCase() !== actor.toUpperCase()
        ? { editedBy: actor, editedAt: now } : {}),
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    });
    setEditing(null);
    setForm(blank());
  }

  async function setStatus(p: RuleProposal, status: RuleProposal['status']) {
    await store.saveProposal({ ...p, status, updatedAt: new Date().toISOString() });
  }

  async function toggleOnDocket(p: RuleProposal) {
    const order = docket?.order ?? [];
    const next = order.includes(p.id) ? order.filter((x) => x !== p.id) : [...order, p.id];
    await store.saveDocket({
      season: SEASON, ...docket, order: next, updatedAt: new Date().toISOString(),
    });
    await setStatus(p, order.includes(p.id) ? 'submitted' : 'docketed');
  }

  async function move(p: RuleProposal, by: number) {
    const order = [...(docket?.order ?? [])];
    const i = order.indexOf(p.id);
    const j = i + by;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j]!, order[i]!];
    await store.saveDocket({ season: SEASON, ...docket, order, updatedAt: new Date().toISOString() });
  }

  /**
   * Open the editor on a proposal.
   *
   * Reachable from the meeting screen as well as from the list, because 2019-I-2 §2 lets the
   * commissioners tidy wording and the moment they actually do it is in the room, with somebody
   * reading the section out. Editing marks the proposal as modified, which is what tells the
   * proposer he has a veto to exercise.
   */
  function startEdit(p: RuleProposal) {
    setEditing(p.id);
    setForm({ title: p.title, body: openBody(p.body), amends: p.amends.join(', ') });
  }

  async function publish() {
    /*
     * SPREAD THE EXISTING DOCKET rather than rebuilding it. This used to name the fields it kept
     * and so quietly dropped the rest - attendance names, the open item, what had already closed -
     * which nobody noticed while "publish" only ever happened once, and which "Re-publish" turns
     * into losing the register in the middle of a meeting.
     */
    /*
     * THE CARRYOVERS GO ON BEFORE THE ANNOUNCEMENT, always. A commissioner can add them earlier
     * to set their running order; if he has not, publishing is the last honest moment to do it,
     * because an agenda that omits them is an agenda on which two rules lapse without a vote.
     */
    /*
     * AND RE-PUBLISHING UN-STARTS A MEETING THAT NEVER HAPPENED.
     *
     * `openItem` sets `meetingOpenedAt` as a side effect of opening the first item, and nothing
     * used to take it off — so one accidental tap moved the league from phase 2 to phase 3 for
     * good, hiding everything phase 2 offers (the GMs' private pre-meeting vote, most of all)
     * with no way back. Tabling now undoes it at the source; this is the recovery for a docket
     * already stuck that way, on a button that is already on the screen.
     *
     * Only when nothing has been closed or recorded. If the committee has actually decided
     * something, re-publishing the agenda does not get to rewind the evening.
     */
    const order = await seedCarryovers();
    const base = !docket || meetingHappened(docket, agenda) ? docket : notStarted(docket);
    await store.saveDocket({
      ...base,
      season: SEASON,
      order,
      publishedAt: new Date().toISOString(),
      publishedBy: actor,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * OPEN THE MEETING — which is opening item 1, and nothing else.
   *
   * There is no separate "start" flag to set: `openItem` writes `meetingOpenedAt` as part of
   * opening the first item, and the phase is derived from that. A meeting is a docket with
   * something open on it, which is the same reason tabling the last item un-starts one that never
   * really happened.
   */
  async function openMeeting() {
    const first = agenda[0];
    if (!docket || !first) return;
    await store.saveDocket(openItem(docket, first.id, new Date().toISOString()));
  }

  /** The one irreversible act. What it writes is spelled out on the bar before it fires. */
  async function commitRecord() {
    if (!docket) return;
    const now = new Date().toISOString();
    await store.saveDocket({
      ...docket,
      openItemId: undefined,
      closedIds: agenda.map((p) => p.id),
      committedAt: now,
      committedBy: actor.toUpperCase(),
      updatedAt: now,
    });
  }

  /** Put them on now, so they can be ordered before the league sees the agenda. */
  async function addCarryovers() {
    const order = await seedCarryovers();
    await store.saveDocket({
      season: SEASON, ...docket, order, updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Take the agenda back, which is a retraction rather than an undo — see `canUnpublish`.
   *
   * Recorded in the audit log, because "the docket was published" is a thing nine people saw and
   * a thing they will later remember differently. The row is the evidence that it was pulled.
   */
  async function unpublish() {
    if (!docket || !canUnpublish(docket).allowed) return;
    note(store, actor, {
      kind: 'delete',
      what: `Unpublished the ${SEASON} docket — ${agenda.length} item`
        + `${agenda.length === 1 ? '' : 's'}, published by ${docket.publishedBy}`,
      target: `dockets/${SEASON}`,
    });
    await store.saveDocket({ ...unpublished(docket), updatedAt: new Date().toISOString() });
  }

  /**
   * Attendance, kept as NAMES with the count derived.
   *
   * The thresholds turn on the count, but the screen needs to know who: 5b draws an absent GM a
   * dashed cell rather than three dead buttons, and a commissioner marking attendance is thinking
   * about people. Storing the count alone meant the screen could say "9 of 10" and had no way to
   * say which one — so it drew ten live rows and let somebody vote on behalf of a man who was not
   * there.
   */
  async function setAbsent(absent: string[]) {
    /*
     * SEAT-HOLDERS ONLY, and the filter is not defensive tidying. `represented` is `LEAGUE_SIZE`
     * minus this list, so a name in it that is not one of the ten subtracts a body the ten never
     * had — and `represented` is what 2015-I-1's relaxed six is measured against. A register
     * saved while Neel was wrongly seated would have carried that error forward into every
     * threshold on the docket. The register is drawn from `GMS`, so nothing can add such a name
     * today; this is what stops one already stored from counting.
     */
    const seats = new Set(GMS.map((g) => g.toUpperCase()));
    const clean = [...new Set(absent.map((g) => g.toUpperCase()))].filter((g) => seats.has(g));
    await store.saveDocket({
      season: SEASON, order: docket?.order ?? [], ...docket,
      absent: clean,
      represented: LEAGUE_SIZE - clean.length,
      updatedAt: new Date().toISOString(),
    });
  }

  /*
   * `castVote` and `recordOutcome` moved to `Meeting`, which is the only place a vote can now be
   * cast — behind `canVote`, on the item a commissioner has opened. Recording the outcome moved
   * with it for the same reason: the tally and the act of writing it down belong on the same
   * screen as the vote, or the two can disagree.
   *
   * The absent-voter filter went too, and is worth naming: an absent man's vote is not a vote, so
   * `Meeting` counts against `docket.represented` rather than against ten.
   */

  /**
   * Branch an amendment off a failed item, in the room.
   *
   * The new proposal goes on the docket IMMEDIATELY AFTER its parent, which is where the argument
   * is: the minutes then read "3 failed, 3a passed" in the order it happened rather than sending
   * a reader to the bottom of the agenda to find out how it ended.
   */
  async function branch(parent: RuleProposal) {
    const now = new Date().toISOString();
    const draft = branchFrom({
      parent, by: actor, now,
      title: `${parent.title} — amended`,
      body: parent.body,
    });
    const id = `${parent.id}-b${Date.now().toString(36)}`;
    await store.saveProposal({ ...draft, id, amends: draft.amends } as RuleProposal);

    const order = [...(docket?.order ?? [])];
    const at = order.indexOf(parent.id);
    // After the parent AND after any siblings it already has, so `3a` never lands before `3`.
    let insert = at < 0 ? order.length : at + 1;
    while (insert < order.length
      && proposals.find((x) => x.id === order[insert])?.branchedFrom === parent.id) insert += 1;
    order.splice(insert, 0, id);
    await store.saveDocket({
      season: SEASON, ...docket, order, updatedAt: now,
    });
  }

  /*
   * THE CONSOLE IS ONE BAR FOR THE WHOLE PAGE, not an amber patch inside whichever card the act
   * happens to belong to. That was the handoff's objection and it is the right one: the three
   * acts here are the three moments the committee year moves, and a control that moves the year
   * should not look like a control that edits a row.
   */
  const consoleBar = commish && docket !== undefined && (
    <CommishConsole
      phase={phase} docket={docket} agenda={agenda} proxies={proxies}
      plan={commitPlan(agenda)} missing={missing.length} represented={room.represented}
      actor={actor} dark={phase === 'meeting'}
      onAct={(act: Exclude<ConsoleAct, null>, itemId?: string) => {
        if (act === 'publish' || act === 'republish') void publish();
        else if (act === 'open-meeting') void openMeeting();
        /*
         * THE BAR'S PRIMARY FOLLOWS THE STATE, so it now performs the two acts that used to live
         * only on the rows: opening the next item and recording the one on the floor. The rows
         * keep theirs — a commissioner working down the list should not have to travel to the
         * bar for each one — and both paths call the same two functions, so they cannot drift.
         */
        else if (act === 'open-item' && itemId) {
          if (docket) void store.saveDocket(openItem(docket, itemId, new Date().toISOString()));
        } else if (act === 'record' && itemId) {
          const item = agenda.find((x) => x.id === itemId);
          if (item) void recordFromBar(item);
        } else void commitRecord();
      }}
      onReversible={(id) => {
        /*
         * UN-START IS AN ACT, not a jump. It is in the reversible cluster because it is
         * reversible — `Open the meeting` puts it straight back — and `unstartMeeting` refuses on
         * its own if anything has been decided since the button was drawn.
         */
        /*
         * HOLDING AN ITEM OPEN is the choice NOT to record it yet, so it does exactly nothing —
         * and saying so beats a button that silently no-ops. It closes the arming step and
         * leaves the floor where it is.
         */
        if (id === 'hold') return;
        if (id === 'unstart') {
          if (docket) void store.saveDocket(unstartMeeting(docket, new Date().toISOString(), agenda));
          return;
        }
        /*
         * THE REVERSIBLE ACTIONS NAVIGATE; they do not act. Reordering a docket and re-wording an
         * item are per-item edits with their own controls, and a bar that performed either would
         * have to guess which item. The handoff's third reversible action, "Suggest wording", is
         * per-item here for that reason — it lands on the running order, where each row carries
         * its own.
         */
        setPane('docket');
        window.setTimeout(() => {
          const target = id === 'attendance' ? '.dkroom' : '.dkorder';
          document.querySelector(target)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }, 60);
      }}
    />
  );

  /*
   * IN SESSION, THE ROOM IS THE PAGE.
   *
   * Measured on a phone before this: the live item started at y=1065 in an 844px viewport. Above
   * it sat a page title, a Mine/Docket switcher, a deadline strip and a four-step phase bar —
   * about 500px of furniture, every bit of it about getting TO the meeting, on the one screen
   * where the meeting is already happening. You scrolled a screen and a half to find the vote.
   *
   * So the three pieces of chrome stand down while an item is open, and each for its own reason
   * rather than to save space:
   *
   *   THE PHASE BAR says which of four stages the year is in. The room's own status bar says
   *   "IN SESSION · item 2 of 6" — the same fact, at the resolution the evening actually needs.
   *
   *   THE DEADLINE STRIP was the worst of them: red, and reading "committee meets Wed, Aug 26,
   *   8:00 PM" while the committee was visibly in session two inches below it. Both its facts are
   *   history by then.
   *
   *   THE TITLE AND PANE SWITCHER go under the room instead of over it. A commissioner still
   *   needs the docket pane mid-meeting — to reorder, to mark somebody in — so they do not
   *   disappear.
   */
  const inSession = phase === 'meeting';

  const proxyBlock = docket && published && agenda.length > 0
    && (phase === 'docket' || (phase === 'meeting' && isLodged(myProxy))) && (
    <div className="proxywrap">
      <ProxyPanel gm={gm} season={SEASON} agenda={agenda} ballot={myProxy}
        openItemId={docket.openItemId}
        onSave={(b) => { setMyProxy(b); void store.saveProxy(b); }} />
      <ProxyVisibility lodged={isLodged(myProxy)} items={instructedCount(myProxy)} />
    </div>
  );

  const chromeAbove = (
    <>
      <header className="pagehead">
        <div>
          <h1>Rule proposals</h1>
        </div>
        {/*
          NOT A `Mine` / `Docket` FILTER (`design_handoff_docket`). It filtered nothing — both
          sides listed the same proposals — and it existed only because the page held two
          duplicate lists. One docket now holds every item once, with ownership as a mark on the
          row that has it.

          What is left is a genuine switch between two different jobs: the docket everybody reads,
          and the commissioners' working area behind it — the inbox, the running order, the
          register. Named for what they are, and only a commissioner has the second one.
        */}
        {commish && (
          <div className="panes small">
            <button type="button" className={`pane ${pane === 'mine' ? 'on' : ''}`}
              onClick={() => setPane('mine')}>The docket</button>
            <button type="button" className={`pane ${pane === 'docket' ? 'on' : ''}`}
              onClick={() => setPane('docket')}>
              Commissioners
              {inbox.length > 0 && <span className="chip warning">{inbox.length} new</span>}
            </button>
          </div>
        )}
      </header>

      {/*
        THE DEADLINE STRIP IS FOR THE DEADLINE. Once the docket is published both of its facts are
        history — submissions closed, and the committee date is either tonight or last week — and
        the docket's own footer says the first of them in the place it matters. A red bar restating
        a passed date above a published agenda is 46px of alarm about nothing.
      */}
      {!inSession && !published && (
        <div className={`deadline ${gate.open ? (gate.urgent ? 'urgent' : '') : 'closed'}`}>
          <span className="ico" aria-hidden="true">{gate.open ? '◷' : '■'}</span>
          <span>
            {settings?.proposalDeadline
              ? `Proposals ${gate.open ? 'close' : 'closed'} ${bothZones(settings.proposalDeadline)}`
              : 'No proposal deadline announced'}
            {settings?.committeeDate && ` · committee ${phase === 'committed' ? 'met' : 'meets'} `
              + `${bothZones(settings.committeeDate)}`}
          </span>
        </div>
      )}

      {/*
        WHERE THE YEAR IS, derived from the docket and never chosen. Publishing moves it to
        phase 2, opening the first item to phase 3, committing to phase 4 — three acts, each
        with its own meaning. The prototype's phase switcher is a prototype affordance.
      */}
      {!inSession && (
        <ol className="phasebar" aria-label="Where the committee year is">
          {(['writing', 'docket', 'meeting', 'committed'] as Phase[]).map((ph, i) => (
            <li key={ph} className={`phasestep ${ph === phase ? 'now' : ''} `
              + `${['writing', 'docket', 'meeting', 'committed'].indexOf(phase) > i ? 'done' : ''}`}>
              <span className="phaseno">{i + 1}</span>
              <span>{PHASE_LABEL[ph]}</span>
            </li>
          ))}
        </ol>
      )}
    </>
  );

  return (
    <div className={`app ${commish && phase !== 'committed' ? 'hasconsole' : ''} `
      + `${inSession ? 'insession' : ''}`}>
      {consoleBar}
      <Linkify>
        {!inSession && chromeAbove}

        {/*
          Phases 2 to 4. It is above the writing pane deliberately: once the docket is out, what
          the league wants from this page is the docket, not the box it was written in.
        */}
        {/*
          PROXY, BEFORE THE ROOM. It comes above the meeting block because it is the thing an
          absent GM has to do BEFORE the evening — 2022-committee-quorum-voting §1 is about
          voting in advance — and once the docket is out it is the only thing on this page some
          people will ever use.

          Not in the writing phase: there is no docket to instruct against. Not once the record is
          committed: the evening is over.

          AND ONCE THE MEETING IS OPEN, only for somebody who actually lodged one — so he can
          still withdraw it before his item is called, which is the one thing left to do with a
          proxy at that point. Asking "can you make the Competition Committee?" while the
          committee is visibly in session, two inches above the room, is the app not knowing what
          time it is.
        */}
        {/* AND IT TOO GOES UNDER THE ROOM once the room exists. Withdrawing a proxy is a real
            thing to do mid-meeting; it is not the thing on the screen. */}
        {!inSession && proxyBlock}

        {/*
          THE ROOM IS FOR THE EVENING, and only for the evening (`design_handoff_docket`, defect 1
          and defect 3).

          It used to render from the moment the docket was published, which put a locked copy of
          all six items — greyed, with a `Locked` chip, a "the meeting has not reached this item"
          line and a dead `Open this item` button on each — directly above the docket card showing
          the same six. Two lists of one thing, and the taller of them was a wall of disabled
          content on a night that had not started.

          Before it: the docket card. During it: the room. After it: the docket card again, as the
          record. One list per phase, and each is the right object for its phase.
        */}
        {docket && phase === 'meeting' && (
          <Meeting phase={phase} agenda={agenda} docket={docket} proxies={proxies}
            gm={gm} actor={actor} role={role} store={store} />
        )}

        {/* Under the room, not over it — see `inSession` above. */}
        {inSession && proxyBlock}
        {inSession && chromeAbove}

        {/*
          PROVISIONAL RULES ARE ON THE DOCKET WHETHER ANYBODY PROPOSES THEM OR NOT. Two 2025 rules
          — the farm draft's second round and the keeper apron — lapse in 2026 without a
          super-majority re-vote, so they are listed here as automatic items rather than waiting
          for somebody to remember them. `provisional` has been in the rulebook data since it was
          generated and was simply not declared on the type, which is why nothing showed it.
        */}
        {phase === 'writing' && revotes.length > 0 && (
          <section className="card">
            <h3 className="striphead">Up for automatic re-vote</h3>
            <RevoteItems rules={revotes} overrides={overrides} commish={commish}
              actor={actor} season={SEASON}
              onSave={async (o) => {
                await store.saveOverride(o);
                setOverrides(await store.overrides(SEASON));
              }} />
            <p className="note mut tight">
              In force now, and gone after the meeting unless seven vote to keep them.
              {commish && ' You can tidy how either one reads before it goes on the agenda.'}
            </p>
          </section>
        )}

        {(pane === 'mine' || !commish) && (
          <>
            {/*
              THE WRITING SURFACE, and nothing else.
              It used to be "Your proposals" — every one of yours rendered in full, including the
              ones already on the docket, which then rendered again below in full. A saved draft
              and a docketed proposal are two records of one intent (`design_handoff_docket`
              defect 1); the docket carries the intent, and what is left here is the box you write
              in and the drafts that have not reached it.
            */}
            <section className="card">
              <div className="striptop">
                <h3 className="striphead">Write a proposal</h3>
                <button type="button" className="btn primary"
                  disabled={!gate.open && !commish}
                  onClick={() => { setEditing('new'); setForm(blank()); }}>
                  Write a proposal
                </button>
              </div>

              {editing === 'new' && (
                <Editor form={form} setForm={setForm} onCancel={() => setEditing(null)}
                  onSaveDraft={() => void save('draft')}
                  onSubmit={() => void save('submitted')} behalf={commish} />
              )}

              {unsent.length === 0 && editing !== 'new' && (
                <p className="note">
                  {mine.length > 0
                    ? 'Everything of yours is on the docket below.'
                    : 'Nothing yet. Two 2025 rules — the two-round farm draft and the keeper '
                      + 'apron — lapse this year without a re-vote, so there is at least that '
                      + 'much on the agenda.'}
                </p>
              )}

              {/*
                TITLES AND ACTIONS, NOT BODIES. A draft you are still writing is a thing to open,
                not a thing to read on a list — and the body is two thousand words behind the
                editor's own button.
              */}
              <ul className="proposallist tight">
                {unsent.map((p) => (
                  <li key={p.id}>
                    <GmBadge gm={p.gm} />
                    <div className="grow">
                      {editing === p.id ? (
                        <Editor form={form} setForm={setForm} onCancel={() => setEditing(null)}
                          onSaveDraft={() => void save('draft', p)}
                          onSubmit={() => void save('submitted', p)} />
                      ) : (
                        <>
                          <b>{p.title}</b>
                          <Transcribed p={p} />
                          {p.editedBy && (
                            <p className="note">
                              Wording edited by {p.editedBy} — read it before the meeting.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    <span className="propactions">
                      <span className={`chip ${chipTone(p.status)}`}>{STATUS_LABEL[p.status]}</span>
                      {/*
                        A DRAFT IS PRIVATE, SO THE DEADLINE HAS NO CLAIM ON IT (Tim, 27 August).

                        All three controls used to hang on `gate.open`, which meant that the
                        moment proposals closed a draft became a row that could not be edited,
                        submitted or deleted — permanently, by anybody. Tim's D/ST draft has sat
                        there since August unable to be thrown away.

                        The deadline exists to stop things REACHING the league late. It has
                        nothing to say about a private document nobody but its author can see:
                        editing one changes nothing anybody has read, and deleting one cannot
                        sneak anything in. So the two harmless acts are always available on a
                        draft, and only `submit` — the one that puts it in front of the
                        commissioners — waits for the gate or a commissioner.

                        A SUBMITTED proposal is a different object: the commissioners have it, and
                        changing or withdrawing it after the close is a real act with a real
                        effect. Those stay gated.
                      */}
                      {editing !== p.id && (p.status === 'draft' || p.status === 'submitted') && (
                        <>
                          {(p.status === 'draft' || gate.open || commish) && (
                            <button type="button" className="linkbtn tiny"
                              onClick={() => startEdit(p)}>edit</button>
                          )}
                          {p.status === 'draft' && (gate.open || commish) && (
                            <button type="button" className="linkbtn tiny"
                              onClick={() => void setStatus(p, 'submitted')}>submit</button>
                          )}
                          {/*
                            AND WHY THE SUBMIT BUTTON IS NOT THERE, on the one row where its
                            absence is a question. A control that silently disappears reads as a
                            bug; the same absence with a reason beside it reads as the deadline
                            working.
                          */}
                          {p.status === 'draft' && !gate.open && !commish && (
                            <span className="mut tiny">too late to submit</span>
                          )}
                          {/*
                            ARMED, because it is the one control here that cannot be undone.
                            Everything else on this row is reversible in a tap — an edit can be
                            edited back, a submission withdrawn — and a two-thousand-word proposal
                            leaving on a mis-tap is not. Same pattern as `Start over` on the
                            ranking, and it disarms itself rather than sitting there mid-question.
                          */}
                          {(p.status === 'draft' || gate.open || commish) && (
                            arming === p.id ? (
                              <>
                                <span className="mut tiny">delete it for good?</span>
                                <button type="button" className="linkbtn tiny danger"
                                  onClick={() => { setArming(null); void discard(p); }}>
                                  delete
                                </button>
                                <button type="button" className="linkbtn tiny"
                                  onClick={() => setArming(null)}>keep</button>
                              </>
                            ) : (
                              <button type="button" className="linkbtn tiny"
                                onClick={() => setArming(p.id)}>delete</button>
                            )
                          )}
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            {/*
              ONE DOCKET, ONE ROW PER PROPOSAL, EXACTLY ONCE. See `domain/docket.ts`.
            */}
            {published && phase !== 'meeting' ? (
              <DocketCard
                phase={phase === 'committed' ? 'committed' : 'docket'}
                seats={LEAGUE_SIZE}
                agenda={agenda}
                openItemId={docket?.openItemId}
                closedIds={docket?.closedIds}
                me={gm}
                gists={GISTS}
                staleDrafts={staleDraftsHere}
                represented={room.represented}
                bodyOf={(id) => agenda.find((x) => x.id === id)?.body ?? ''}
                recordOf={(id) => agenda.find((x) => x.id === id)}
                closedLabel={settings?.proposalDeadline
                  ? bothZones(settings.proposalDeadline) : undefined}
                committedLabel={docket?.committedAt
                  ? bothZones(docket.committedAt) : undefined}
                onDiscardDraft={(id) => {
                  const d = unsent.find((x) => staleDraftsHere[id] && sameIntent(x.title,
                    agenda.find((a) => a.id === id)?.title ?? ''));
                  if (d) void discard(d);
                }}
              />
            ) : !published ? (
              <section className="card">
                <h3 className="striphead">The docket</h3>
                <p className="note">
                  Not published yet. The commissioners set the agenda after the deadline; once
                  they publish it, everything on it appears here so absent GMs can vote in
                  advance (<Citations ids={['2022-committee-quorum-voting']} />).
                </p>
              </section>
            ) : null}
          </>
        )}

        {pane === 'docket' && commish && (
          <>
            <section className="card gated commish">
              <GateTag level="commish" what="Commissioners — the docket before the league sees it" />
              <div className="striptop">
                <h3 className="striphead">Submitted — {inbox.length} waiting</h3>
                {/*
                  ENTERING ONE THAT ARRIVED SOMEWHERE ELSE (Tim, 22 August). The 2026 proposals
                  reached Vishal and Rajan by email and Discord months before this screen existed.
                  The button lives HERE rather than on "Mine" because this is the list it feeds and
                  the pane the commissioners work in — one that landed in the author's own list
                  would be filed correctly and invisible to the man who typed it.
                */}
                <button type="button" className="btn primary"
                  onClick={() => { setEditing('behalf'); setForm(blank()); }}>
                  Enter one that came in another way
                </button>
              </div>
              <p className="note">
                Tidy the wording here before it goes on the agenda. 2019-I-2 §2 allows it — a
                modification is still the same rule and still needs a super-majority — and
                2020-I-1 §2 gives the original proposer a veto over any change he does not accept.
              </p>

              {editing === 'behalf' && (
                <Editor form={form} setForm={setForm} onCancel={() => setEditing(null)}
                  behalf
                  draftLabel="Hold as a draft" submitLabel="Enter it as submitted"
                  onSaveDraft={() => void save('draft')}
                  onSubmit={() => void save('submitted')} />
              )}
              <ul className="proposallist">
                {inbox.map((p) => (
                  <li key={p.id}>
                    <GmBadge gm={p.gm} />
                    <div className="grow">
                      {editing === p.id ? (
                        <Editor form={form} setForm={setForm} onCancel={() => setEditing(null)}
                          onSaveDraft={() => void save('submitted', p)}
                          onSubmit={() => void save('docketed', p)}
                          draftLabel="Save wording" submitLabel="Save and docket" />
                      ) : (
                        <>
                          <b>{p.title}</b>
                          <RichView html={p.body} className="mut" />
                          <Transcribed p={p} />
                        </>
                      )}
                    </div>
                    <span className="propactions">
                      {editing !== p.id && (
                        <>
                          <button type="button" className="linkbtn tiny" onClick={() => {
                            setEditing(p.id);
                            setForm({ title: p.title, body: openBody(p.body), amends: p.amends.join(', ') });
                          }}>edit wording</button>
                          <button type="button" className="btn small"
                            onClick={() => void toggleOnDocket(p)}>add to docket</button>
                        </>
                      )}
                    </span>
                  </li>
                ))}
                {inbox.length === 0 && <li className="note">Nothing waiting.</li>}
              </ul>
            </section>

            <section className="card">
              <div className="striptop">
                <h3 className="striphead">Agenda — {agenda.length} item{agenda.length === 1 ? '' : 's'}</h3>
                {missing.length > 0 && (
                  /*
                   * LOUD, because the failure is silent. Two rules lapse on the night if they
                   * never reach a vote, and an agenda that is simply short gives nobody a reason
                   * to look. Publishing adds them anyway - this is here so they can be ordered
                   * first, and so the reason is on screen rather than in a commit message.
                   */
                  <p className="note bad carryoverwarn">
                    {missing.length === 1 ? 'One carryover re-vote is' : `${missing.length} carryover re-votes are`}
                    {' '}not on the docket yet — {missing.map((r) => splitRuleTitle(r.title).title).join(' and ')}.
                    {' '}They lapse without a super-majority (<Citations ids={['2019-I-2']} />), so they have
                    to be voted on. Publishing puts them on; add them now if you want to set the order.
                    {' '}
                    <button type="button" className="linkbtn tiny" onClick={() => void addCarryovers()}>
                      add them now
                    </button>
                  </p>
                )}
                <span className="actions inline">
                  <span className="declstatus">
                    {published
                      ? `Published ${new Date(docket!.publishedAt!).toLocaleString()} by ${docket!.publishedBy}`
                      : 'Not published'}
                  </span>
                  {/*
                    ALSO ENABLED WHEN THE ONLY BUSINESS IS A CARRYOVER. A year in which nobody
                    proposes anything still has two rules owing a re-vote, and gating publish on
                    the agenda already having items would make that the one year the committee
                    cannot be given an agenda at all.
                  */}
                  {/*
                    PUBLISHING LIVES ON THE CONSOLE, not here. Two buttons doing one irreversible
                    thing is two places to check when somebody asks whether it went out — and the
                    console is the one that arms first and names who pressed it.

                    Re-publish stays, because it is not the same act: it is the recovery for a
                    docket whose order changed or which got stuck in a meeting that never
                    happened, and it belongs beside the order it re-announces.
                  */}
                  {published && (
                    <button type="button" className="btn primary"
                      disabled={agenda.length === 0 && missing.length === 0}
                      onClick={() => void publish()}>
                      Re-publish
                    </button>
                  )}
                  {published && (
                    /*
                     * Only while it is still only an announcement. `canUnpublish` says why not,
                     * in the words the button shows, rather than the button vanishing silently.
                     */
                    <button type="button" className="btn"
                      disabled={!canUnpublish(docket).allowed}
                      title={canUnpublish(docket).why}
                      onClick={() => void unpublish()}>
                      Un-publish
                    </button>
                  )}
                </span>
              </div>

              {/*
                5b: one item fills the screen, attendance is a header fact with its consequence
                spelled out, and the record button carries the outcome it will write. The old
                accordion of all four items with a `represented` select buried in a form is what
                this replaces.
              */}
              {/*
                6a sits ABOVE the meeting screen, because it is done before it: declare what each
                item changes, then vote. Both read the same agenda, so a branch created in the
                room appears here undeclared rather than silently doing nothing.
              */}
              <DeclareEffect
                agenda={agenda}
                season={SEASON}
                facts={seasonFacts}
                onSave={(p2, effect) => void store.saveProposal({
                  ...p2, effect, updatedAt: new Date().toISOString(),
                })}
              />

              {/*
                THE VOTING UI IS GONE FROM HERE, and that is acceptance check 7.

                `DocketVote` rendered three vote buttons per GM as soon as a commissioner opened
                this pane — before the docket was published, let alone before the meeting reached
                the item. The whole point of the meeting is that it is an argument, and a docket
                that can be voted through in advance is a docket nobody argues about. Voting now
                lives in `Meeting`, behind `canVote`, which refuses on four separate grounds.

                What is left here is what this pane was actually for: the running order, who is in
                the room, and the wording — none of which is a vote.
              */}
              <DocketOrder
                agenda={agenda}
                docket={docket}
                onMove={(p2, by) => void move(p2, by)}
                onRemove={(p2) => void toggleOnDocket(p2)}
                onSetAbsent={(a) => void setAbsent(a)}
                onBranch={(p2) => void branch(p2)}
                onEditWording={(p2) => { setPane('mine'); startEdit(p2); }}
                onToggleObjection={(p2) => void store.saveProposal({
                  ...p2, proposerObjects: !p2.proposerObjects, updatedAt: new Date().toISOString(),
                })}
              />
            </section>
          </>
        )}
      </Linkify>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------- */

/*
 * `VoteRow` lived here and is gone. It drew every docket item as a row in an accordion, which is
 * the shape 5b replaces: the room votes on one thing at a time and the wording has to be on
 * screen while it does. Everything it did now lives in `DocketVote`, one item at a time.
 */

/**
 * "Ari's proposal, entered by Tim" — said wherever the proposal is shown.
 *
 * It is the author's name on the row and that is right: he wrote it, and 2020-I-1 §2 gives him a
 * veto over changes made in the room. But a proposal that appeared under his name having never
 * touched the app would be the screen claiming he used it, so the second fact goes beside the
 * first rather than replacing it — the same rule the activity log follows for impersonation.
 */
function Editor(
  {
    form, setForm, onCancel, onSaveDraft, onSubmit,
    draftLabel = 'Save draft', submitLabel = 'Submit', behalf = false,
  }: {
    form: Form;
    setForm: (f: Form) => void;
    onCancel: () => void; onSaveDraft: () => void; onSubmit: () => void;
    draftLabel?: string; submitLabel?: string;
    /** Commissioners only, and only on a NEW one — see `onBehalfOf` on the form. */
    behalf?: boolean;
  },
) {
  return (
    <div className="formstack">
      {behalf && (
        /*
         * TRANSCRIBING SOMEBODY ELSE'S PROPOSAL. The author is the man who wrote it, not the man
         * at the keyboard — 2020-I-1 §2 gives the author a veto over modifications made in the
         * room, and filing it under the typist would put that veto in the wrong hands. So the
         * picker sets `gm`, and `enteredBy` records who transcribed it.
         */
        <div className="propbehalf">
          <label>
            Whose proposal is it?
            <select value={form.onBehalfOf ?? ''}
              onChange={(e) => setForm({ ...form, onBehalfOf: e.target.value })}>
              <option value="">Mine</option>
              {GMS.map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
            </select>
          </label>
          <label>
            How it reached you (optional)
            <input value={form.enteredVia ?? ''} placeholder="Emailed 3 July · Discord #rules"
              onChange={(e) => setForm({ ...form, enteredVia: e.target.value })} />
          </label>
          {form.onBehalfOf && (
            <p className="note">
              It will appear as <b>{managerShort(form.onBehalfOf)}’s</b> proposal, marked as entered
              by you. He keeps the 2020-I-1 §2 veto over any wording change made in the room.
            </p>
          )}
        </div>
      )}
      <label>
        Title
        <input value={form.title} placeholder="Expand the farm draft to two rounds"
          onChange={(e) => setForm({ ...form, title: e.target.value })} />
      </label>
      <label>
        Amends (rule ids, optional)
        <input value={form.amends} placeholder="2019-II-1, 2015-III-7"
          onChange={(e) => setForm({ ...form, amends: e.target.value })} />
      </label>
      <div className="richfield">
        <span className="richlabel">The proposal</span>
        <RichEditor value={form.body} label="The proposal"
          placeholder={'Section 1: …  —  Section 2: …  —  Explanation: …'}
          onChange={(html) => setForm({ ...form, body: html })} />
      </div>
      <div className="actions">
        <button type="button" className="linkbtn" onClick={onCancel}>cancel</button>
        <button type="button" className="btn" onClick={onSaveDraft}>{draftLabel}</button>
        <button type="button" className="btn primary" onClick={onSubmit}>{submitLabel}</button>
      </div>
    </div>
  );
}

const chipTone = (s: RuleProposal['status']) =>
  s === 'passed' ? 'good'
    : s === 'provisional' ? 'warning'
      : s === 'failed' || s === 'withdrawn' ? 'critical'
        : '';
